import { SceneEngine, SceneID } from '@/enums/agent'
import type { ModelConfigItem } from '@/lib/api/models/config'
import { parseDirectorPayload } from '@/lib/utils/director-restore'
import { safeJsonParse } from '@/lib/utils/transform'

interface DirectorDataLike {
  segments?: unknown
}

/** Minimal sceneParams shape for reading director `ext` from tasks. */
export interface DirectorSceneParamsLike {
  ext?: string
}

export type DirectorSceneParamsInput = DirectorSceneParamsLike | null | undefined

/** Backend submit payload for director console tasks. */
export const DIRECTOR_SUBMIT_SCENE_ID = SceneID.RM3_5
export const DIRECTOR_SUBMIT_ENGINE = SceneEngine.RM3_5

/** User-selected model scene id stored inside director `ext` for task list display. */
export const DIRECTOR_SOURCE_SCENE_ID = 'source_scene_id'

/** Marks director ext produced by RM3.2D auto pipeline (Adjust restores prompt, not chip). */
export const DIRECTOR_AUTO_PIPELINE_FLAG = 'auto_director'

/**
 * Prompt-bar resolution value (e.g. "720p") stored on auto-director ext so task cards
 * show the same label as the external resolution control — not pixel WxH short-edge.
 */
export const DIRECTOR_PROMPT_RESOLUTION = 'prompt_resolution'

const DIRECTOR_DISPLAY_LABEL = 'RM3.2'

export function hasDirectorSegments(
  directorData: DirectorDataLike | Record<string, unknown> | null | undefined
): boolean {
  if (!directorData) return false
  const segments = directorData.segments
  return Array.isArray(segments) && segments.length > 0
}

/** Drop invalid applied director payloads; used on persist read/write. */
export function sanitizeStoredDirectorData(
  directorData: Record<string, unknown> | null | undefined
): Record<string, unknown> | null {
  if (!directorData) return null
  return hasDirectorSegments(directorData) ? directorData : null
}

export function isDirectorTaskSceneId(sceneId: string | number | null | undefined): boolean {
  if (sceneId == null || sceneId === '') return false
  return String(sceneId) === DIRECTOR_SUBMIT_SCENE_ID
}

export function isDirectorTaskEngine(engine: string | null | undefined): boolean {
  return engine === DIRECTOR_SUBMIT_ENGINE
}

export function isDirectorTask(input: {
  sceneId?: string | number | null
  engine?: string | null
}): boolean {
  return isDirectorTaskSceneId(input.sceneId) || isDirectorTaskEngine(input.engine)
}

/** Whether the model can use the director console (admin: supportDirectorConsole). */
export function isDirectorCapableModel(
  model: ModelConfigItem | null | undefined
): boolean {
  return !!model?.supportDirectorConsole || isAutoDirectorModel(model)
}

/**
 * Whether the model runs the automated director pipeline (RM3.2D).
 * On submit these models auto-run: storyboard enhance → grid image →
 * per-frame enhance → crop → build director data, without manual console editing.
 */
export function isAutoDirectorModel(
  model: ModelConfigItem | null | undefined
): boolean {
  return model?.engine === SceneEngine.RM3_2D
}

/**
 * Scene id used when creating a director task.
 * RM3.2D keeps its own model scene id; other director flows still submit as RM3.5.
 * Engine always remains {@link DIRECTOR_SUBMIT_ENGINE}.
 */
export function resolveDirectorSubmitSceneId(
  model: ModelConfigItem | null | undefined
): SceneID {
  if (isAutoDirectorModel(model) && model?.sceneId != null) {
    return model.sceneId
  }
  return DIRECTOR_SUBMIT_SCENE_ID
}

/** Fallback when opening the console from a non-director model (first admin-flagged model). */
export function getDirectorFallbackModel(
  modelConfigs: ModelConfigItem[]
): ModelConfigItem | undefined {
  return modelConfigs.find(model => model.supportDirectorConsole)
}

/**
 * Model to use when opening the director console.
 * Keeps the current model when admin marks it capable; otherwise first capable model.
 */
export function resolveDirectorModelForConsoleOpen(
  modelConfigs: ModelConfigItem[],
  current: ModelConfigItem | null | undefined
): ModelConfigItem | undefined {
  if (current && isDirectorCapableModel(current)) return current
  return getDirectorFallbackModel(modelConfigs)
}

/** Director data is active for submit / preview when segments exist and the model supports the console. */
export function isDirectorSessionActive(
  directorData: DirectorDataLike | Record<string, unknown> | null | undefined,
  model: ModelConfigItem | null | undefined
): boolean {
  return (
    !!directorData &&
    hasDirectorSegments(directorData) &&
    isDirectorCapableModel(model)
  )
}

/** True when ext was produced by the RM3.2D auto pipeline (not manual console Apply). */
export function isAutoDirectorPipelinePayload(
  directorData: Record<string, unknown> | null | undefined
): boolean {
  if (!directorData) return false
  return directorData[DIRECTOR_AUTO_PIPELINE_FLAG] === true
}

/**
 * Manual director console session (chip / skip auto-pipeline).
 * Auto-pipeline payloads keep submit semantics via {@link isDirectorSessionActive}
 * but must not show the prompt-bar chip.
 */
export function isManualDirectorSessionActive(
  directorData: Record<string, unknown> | null | undefined,
  model: ModelConfigItem | null | undefined
): boolean {
  return (
    isDirectorSessionActive(directorData, model) &&
    !isAutoDirectorPipelinePayload(directorData)
  )
}

/**
 * Whether to show the DirectorPreview chip in the prompt bar.
 * Chip and prompt input are mutually exclusive — only one is visible at a time.
 * Auto-director pipeline payloads never show the chip.
 */
export function shouldShowDirectorPreviewChip(
  directorData: DirectorDataLike | Record<string, unknown> | null | undefined,
  model: ModelConfigItem | null | undefined,
  options?: { workDetailMode?: boolean }
): boolean {
  if (!directorData || !hasDirectorSegments(directorData)) return false
  if (isAutoDirectorPipelinePayload(directorData as Record<string, unknown>)) {
    return false
  }
  return (
    options?.workDetailMode === true ||
    isDirectorSessionActive(directorData, model)
  )
}

export function getDirectorSourceSceneId(
  payload: Record<string, unknown> | null | undefined
): string | number | undefined {
  if (!payload) return undefined
  const v = payload[DIRECTOR_SOURCE_SCENE_ID]
  if (v == null || v === '') return undefined
  if (typeof v === 'string' || typeof v === 'number') return v
  return undefined
}

export function resolveDirectorSourceModel(
  payload: Record<string, unknown> | null | undefined,
  modelConfigs: ModelConfigItem[]
): ModelConfigItem | undefined {
  const sourceId = getDirectorSourceSceneId(payload)
  if (sourceId == null) return undefined
  return modelConfigs.find(m => String(m.sceneId) === String(sourceId))
}

/** Director payload produced by RM3.2D auto pipeline (source_scene_id → RM3.2D). */
export function isRm32dGeneratedDirectorPayload(
  payload: Record<string, unknown> | null | undefined,
  modelConfigs: ModelConfigItem[]
): boolean {
  if (!payload) return false
  return isAutoDirectorModel(resolveDirectorSourceModel(payload, modelConfigs))
}

/**
 * RM3.2D auto-pipeline task Adjust → prompt/ref-image mode (no chip).
 * Manual RM3.2D console Apply sets `auto_director: false` to keep chip Adjust.
 */
export function isRm32dAutoDirectorAdjustPayload(
  payload: Record<string, unknown> | null | undefined,
  modelConfigs: ModelConfigItem[],
  displayModel?: ModelConfigItem | null
): boolean {
  if (!payload) return false
  if (payload[DIRECTOR_AUTO_PIPELINE_FLAG] === true) return true
  if (payload[DIRECTOR_AUTO_PIPELINE_FLAG] === false) return false
  return (
    isAutoDirectorModel(displayModel) ||
    isRm32dGeneratedDirectorPayload(payload, modelConfigs)
  )
}

/** Prompt to restore when Adjust reopens an RM3.2D auto-director task for re-generation. */
export function resolveRm32dAdjustPrompt(
  payload: Record<string, unknown> | null | undefined,
  videoGenPrompt?: string
): string {
  const global = payload?.global
  if (global && typeof global === 'object') {
    const globalPrompt = (global as { prompt?: string }).prompt?.trim()
    if (globalPrompt) return globalPrompt
  }
  const scenePrompt = videoGenPrompt?.trim()
  if (scenePrompt) return scenePrompt
  return ''
}

export function resolveDirectorTaskDisplaySceneId(
  taskSceneId: string | number | null | undefined,
  modelConfigs: ModelConfigItem[],
  sceneParams?: DirectorSceneParamsInput,
  rawSceneParamsJson?: string
): SceneID | string | number | null | undefined {
  if (!isDirectorTaskSceneId(taskSceneId)) return taskSceneId

  const payload = parseDirectorPayload(sceneParams ?? undefined, rawSceneParamsJson)
  const sourceId = getDirectorSourceSceneId(payload)
  if (sourceId != null) {
    const matched = modelConfigs.find(m => String(m.sceneId) === String(sourceId))
    if (matched) return matched.sceneId
    return sourceId
  }

  return getDirectorDisplayModel(modelConfigs)?.sceneId ?? taskSceneId
}

export function getDirectorTaskDisplayModel(
  modelConfigs: ModelConfigItem[],
  taskSceneId: string | number | null | undefined,
  sceneParams?: DirectorSceneParamsInput,
  rawSceneParamsJson?: string
): ModelConfigItem | undefined {
  const displaySceneId = resolveDirectorTaskDisplaySceneId(
    taskSceneId,
    modelConfigs,
    sceneParams,
    rawSceneParamsJson
  )
  if (displaySceneId == null) return undefined
  return modelConfigs.find(m => String(m.sceneId) === String(displaySceneId))
}

export function getDirectorTaskDisplayShortName(
  modelConfigs: ModelConfigItem[],
  taskSceneId: string | number | null | undefined,
  sceneParams?: DirectorSceneParamsInput,
  rawSceneParamsJson?: string
): string {
  return (
    getDirectorTaskDisplayModel(modelConfigs, taskSceneId, sceneParams, rawSceneParamsJson)
      ?.shortName ?? getDirectorDisplayShortName(modelConfigs)
  )
}

export function getDirectorDisplayModel(
  modelConfigs: ModelConfigItem[]
): ModelConfigItem | undefined {
  return modelConfigs.find(model => model.engine === SceneEngine.RM3_2)
}

export function getDirectorDisplayShortName(modelConfigs?: ModelConfigItem[]): string {
  return getDirectorDisplayModel(modelConfigs ?? [])?.shortName || DIRECTOR_DISPLAY_LABEL
}

/** Map submit engine to UI tag engine (rm3.5-director → Director；正式 RM3.5 仍走自身 engine). */
export function getDirectorDisplayEngine(engine?: string | null): SceneEngine | undefined {
  if (!engine) return undefined
  if (isDirectorTaskEngine(engine)) return SceneEngine.RM3_5
  return engine as SceneEngine
}

/** Resolve task sceneId for model-config lookup (director submit id → source or RM3.2). */
export function getDirectorDisplaySceneId(
  sceneId: string | number | null | undefined,
  modelConfigs: ModelConfigItem[],
  sceneParams?: DirectorSceneParamsInput,
  rawSceneParamsJson?: string
): SceneID | string | number | null | undefined {
  return resolveDirectorTaskDisplaySceneId(
    sceneId,
    modelConfigs,
    sceneParams,
    rawSceneParamsJson
  )
}

export function parseDirectorExtFromSceneParams(
  sceneParams: { ext?: string } | null | undefined
): Record<string, unknown> | null {
  if (!sceneParams?.ext) return null
  const parsed = safeJsonParse<Record<string, unknown>>(sceneParams.ext)
  if (!parsed || !hasDirectorSegments(parsed)) return null
  return parsed
}

export function isDirectorTaskFromRecord(data: {
  sceneId?: string | number | null
  engine?: string | null
  sceneParams?: { ext?: string; engine?: string } | null
}): boolean {
  if (
    isDirectorTask({
      sceneId: data.sceneId,
      engine: data.engine ?? data.sceneParams?.engine
    })
  ) {
    return true
  }
  return parseDirectorExtFromSceneParams(data.sceneParams ?? undefined) != null
}
